import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ScatterChart, Scatter, Area, AreaChart, PieChart, Pie, Cell } from 'recharts';
import { Table, Card, Row, Col, Button } from 'react-bootstrap';
import { useAppContext } from "../../../libs/contextLib";
import { onError } from "../../../libs/errorLib";
import axiosInstance from "../../../axiosApi";
import { useTranslation } from 'react-i18next';

const ResultReport = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const { selectedItemAppContext } = useAppContext();
  const [sensorData, setSensorData] = useState(
  {
    lidar: [
      {
        timestamp: 1635123456.123,
        topic: "/velodyne_points",
        frame_id: "velodyne",
        points: Array.from({ length: 1000 }, (_, i) => ({
          x: (Math.random() - 0.5) * 100,
          y: (Math.random() - 0.5) * 100,
          z: (Math.random() - 0.5) * 10,
          intensity: Math.random() * 255,
          ring: i % 16
        })),
        statistics: {
          totalPoints: 62847,
          validPoints: 61532,
          minRange: 0.5,
          maxRange: 119.8,
          averageIntensity: 95.4,
          pointsByRing: [3924, 3918, 3912, 3906, 3898, 3890, 3885, 3878, 3872, 3865, 3859, 3852, 3845, 3838, 3831, 3824]
        },
        quality: {
          noiseLevel: 0.023,
          completeness: 0.978,
          symmetryScore: 0.945
        }
      }
    ],
    radar: [
      {
        timestamp: 1635123456.125,
        topic: "/radar/front/tracks",
        frame_id: "radar_front",
        tracks: [
          {
            id: 1,
            position: { x: 25.4, y: -1.2, z: 0.0 },
            velocity: { x: -15.3, y: 0.2, z: 0.0 },
            acceleration: { x: -0.8, y: 0.1, z: 0.0 },
            dimensions: { length: 4.5, width: 1.8, height: 1.6 },
            classification: "car",
            confidence: 0.94,
            rcs: -12.5,
            snr: 18.3,
            range: 25.6,
            azimuth: -2.7,
            elevation: 0.1,
            doppler: -15.1,
            trackAge: 1.2,
            probabilityOfExistence: 0.98
          },
          {
            id: 2,
            position: { x: 45.8, y: 3.5, z: 0.0 },
            velocity: { x: -12.7, y: -0.5, z: 0.0 },
            acceleration: { x: 0.2, y: 0.0, z: 0.0 },
            dimensions: { length: 4.2, width: 1.7, height: 1.5 },
            classification: "car",
            confidence: 0.87,
            rcs: -8.2,
            snr: 22.1,
            range: 45.9,
            azimuth: 4.4,
            elevation: -0.2,
            doppler: -12.4,
            trackAge: 0.8,
            probabilityOfExistence: 0.91
          },
          {
            id: 3,
            position: { x: 12.3, y: -4.1, z: 0.0 },
            velocity: { x: 0.0, y: 0.0, z: 0.0 },
            acceleration: { x: 0.0, y: 0.0, z: 0.0 },
            dimensions: { length: 0.8, width: 0.8, height: 1.8 },
            classification: "pedestrian",
            confidence: 0.76,
            rcs: -25.3,
            snr: 12.8,
            range: 12.9,
            azimuth: -18.4,
            elevation: 0.0,
            doppler: 0.2,
            trackAge: 0.3,
            probabilityOfExistence: 0.82
          }
        ],
        detections: [
          { range: 25.4, azimuth: -2.7, elevation: 0.1, doppler: -15.1, rcs: -12.5, snr: 18.3 },
          { range: 45.8, azimuth: 4.4, elevation: -0.2, doppler: -12.4, rcs: -8.2, snr: 22.1 },
          { range: 12.9, azimuth: -18.4, elevation: 0.0, doppler: 0.2, rcs: -25.3, snr: 12.8 }
        ],
        systemInfo: {
          temperature: 65.2,
          voltage: 12.1,
          currentConsumption: 2.8,
          operatingMode: "normal",
          calibrationStatus: "valid"
        }
      }
    ],
    camera: [
      {
        timestamp: 1635123456.120,
        topic: "/camera/front/image_raw",
        frame_id: "camera_front",
        image: {
          width: 1920,
          height: 1080,
          encoding: "bgr8",
          exposure: 8.33,
          gain: 1.2,
          whiteBalance: { r: 1.0, g: 1.0, b: 1.1 },
          aperture: 2.8,
          focusDistance: 15.0
        },
        detections: [
          {
            id: 1,
            class: "car",
            confidence: 0.94,
            bbox: { x: 450, y: 320, width: 280, height: 180 },
            worldPosition: { x: 25.4, y: -1.2, z: 0.0 },
            trackId: 101,
            attributes: { color: "blue", type: "sedan" }
          },
          {
            id: 2,
            class: "pedestrian",
            confidence: 0.87,
            bbox: { x: 720, y: 280, width: 45, height: 120 },
            worldPosition: { x: 12.3, y: -4.1, z: 0.0 },
            trackId: 102,
            attributes: { pose: "walking", direction: "left_to_right" }
          },
          {
            id: 3,
            class: "traffic_sign",
            confidence: 0.91,
            bbox: { x: 1200, y: 200, width: 60, height: 80 },
            worldPosition: { x: 35.2, y: 2.8, z: 3.5 },
            attributes: { signType: "speed_limit", value: "50", occlusion: 0.0 }
          }
        ],
        quality: {
          brightness: 128.5,
          contrast: 1.2,
          sharpness: 0.85,
          saturation: 1.1,
          noiseLevel: 0.034,
          blur: 0.12,
          overexposedPixels: 0.02,
          underexposedPixels: 0.05
        }
      }
    ],
    fusion: [
      {
        timestamp: 1635123456.125,
        objects: [
          {
            id: 1,
            type: "vehicle",
            confidence: 0.96,
            position: { x: 25.4, y: -1.2, z: 0.0 },
            velocity: { x: -15.3, y: 0.2, z: 0.0 },
            dimensions: { length: 4.5, width: 1.8, height: 1.6 },
            sensorDetections: {
              lidar: { confidence: 0.98, pointCount: 234 },
              radar: { confidence: 0.94, rcs: -12.5, snr: 18.3 },
              camera: { confidence: 0.91, bbox: [450, 320, 280, 180] }
            },
            trackingInfo: {
              age: 1.2,
              predictionError: 0.15,
              associationScore: 0.92
            }
          }
        ],
        sensorHealth: {
          lidar: { status: "ok", confidence: 0.98, lastUpdate: 1635123456.123 },
          radar: { status: "ok", confidence: 0.95, lastUpdate: 1635123456.125 },
          camera: { status: "ok", confidence: 0.93, lastUpdate: 1635123456.120 },
          imu: { status: "ok", confidence: 0.99, lastUpdate: 1635123456.121 },
          gps: { status: "ok", confidence: 0.89, lastUpdate: 1635123456.122 }
        }
      }
    ]
  }
  );

  useEffect(() => {
    console.log(selectedItemAppContext);
    if (selectedItemAppContext) {
      if (selectedItemAppContext.original_filename.split('.').at(-1) === 'bag') {
        console.log("Ros1 Rosbag file")
        
        // axiosInstance.post('/raxigo/ros1/sensor-data/', {
        //   routename: fields.routename,
        //   ref_lat: fields.ref_lat
        // }).then(function (response) {
        //     if (typeof response === 'undefined') throw "에러처리루틴 완료";
        //     // console.log(response.data);
        //     setSensorData(response.data);
        // }).catch(function (error) {
        //     setIsLoading(false);
        // });
      } else {
        console.log("Ros2 Rosbag file")
      }
    }
  }, [selectedItemAppContext]);

  const [selectedAnalysis, setSelectedAnalysis] = useState('overview');
  const [timeWindow, setTimeWindow] = useState('current');

  // 시계열 데이터 생성 (시뮬레이션)
  const timeSeriesData = useMemo(() => {
    return Array.from({ length: 100 }, (_, i) => ({
      time: i,
      timestamp: 1635123456 + i * 0.1,
      // 라이더 메트릭
      lidarPoints: 60000 + Math.sin(i * 0.1) * 5000 + Math.random() * 2000,
      lidarQuality: 0.95 + Math.sin(i * 0.05) * 0.05 + Math.random() * 0.02,
      lidarNoise: 0.02 + Math.random() * 0.01,
      lidarIntensity: 90 + Math.sin(i * 0.08) * 10 + Math.random() * 5,
      // 레이더 메트릭
      radarTracks: Math.floor(2 + Math.sin(i * 0.15) * 2 + Math.random() * 3),
      radarDetections: Math.floor(3 + Math.sin(i * 0.12) * 3 + Math.random() * 4),
      radarSnr: 15 + Math.sin(i * 0.1) * 5 + Math.random() * 3,
      radarRange: 80 + Math.sin(i * 0.07) * 20 + Math.random() * 10,
      // 카메라 메트릭
      cameraDetections: Math.floor(1 + Math.sin(i * 0.18) * 2 + Math.random() * 4),
      cameraConfidence: 0.85 + Math.sin(i * 0.06) * 0.1 + Math.random() * 0.05,
      cameraBrightness: 120 + Math.sin(i * 0.04) * 20 + Math.random() * 10,
      cameraSharpness: 0.8 + Math.sin(i * 0.09) * 0.1 + Math.random() * 0.05,
      // 융합 메트릭
      fusionAccuracy: 0.92 + Math.sin(i * 0.03) * 0.05 + Math.random() * 0.02,
      fusionLatency: 35 + Math.sin(i * 0.11) * 10 + Math.random() * 8,
      systemLoad: 0.6 + Math.sin(i * 0.13) * 0.2 + Math.random() * 0.1
    }));
  }, []);

  // 성능 메트릭 계산
  const performanceMetrics = useMemo(() => {
    const lidar = sensorData.lidar[0];
    const radar = sensorData.radar[0];
    const camera = sensorData.camera[0];
    const fusion = sensorData.fusion[0];

    return {
      lidar: {
        dataRate: 10, // Hz
        pointDensity: lidar.statistics.totalPoints / 1000,
        qualityScore: lidar.quality.completeness * 100,
        noiseLevel: lidar.quality.noiseLevel * 100,
        rangeAccuracy: 0.02, // m
        angularResolution: 0.2 // degrees
      },
      radar: {
        trackingAccuracy: radar.tracks.reduce((sum, track) => sum + track.confidence, 0) / radar.tracks.length * 100,
        detectionRange: Math.max(...radar.detections.map(d => d.range)),
        velocityAccuracy: 0.1, // m/s
        falseAlarmRate: 0.05,
        averageSnr: radar.detections.reduce((sum, d) => sum + d.snr, 0) / radar.detections.length
      },
      camera: {
        detectionAccuracy: camera.detections.reduce((sum, det) => sum + det.confidence, 0) / camera.detections.length * 100,
        imageQuality: camera.quality.sharpness * 100,
        processingLatency: 25, // ms
        falsePositiveRate: 0.03,
        classificationAccuracy: 94.2
      },
      fusion: {
        overallAccuracy: fusion.objects[0].confidence * 100,
        sensorAgreement: 0.91,
        trackingConsistency: fusion.objects[0].trackingInfo.associationScore * 100,
        systemReliability: Object.values(fusion.sensorHealth).reduce((sum, sensor) => sum + sensor.confidence, 0) / 5 * 100
      }
    };
  }, [sensorData]);

  // 센서 상태 데이터
  const sensorHealthData = useMemo(() => {
    const health = sensorData.fusion[0].sensorHealth;
    return Object.entries(health).map(([sensor, data]) => ({
      sensor: sensor.charAt(0).toUpperCase() + sensor.slice(1),
      confidence: data.confidence * 100,
      status: data.status,
      lastUpdate: new Date(data.lastUpdate * 1000).toLocaleTimeString()
    }));
  }, [sensorData]);

  // 객체 탐지 분포 데이터
  const detectionDistribution = useMemo(() => {
    const classes = {};
    sensorData.camera[0].detections.forEach(det => {
      classes[det.class] = (classes[det.class] || 0) + 1;
    });

    return Object.entries(classes).map(([name, count]) => ({
      name: name === 'car' ? '차량' : name === 'pedestrian' ? '보행자' : name === 'traffic_sign' ? '교통표지' : name,
      value: count,
      percentage: (count / sensorData.camera[0].detections.length * 100).toFixed(1)
    }));
  }, [sensorData]);

  // 레이더 극좌표 데이터
  const radarPolarData = useMemo(() => {
    return sensorData.radar[0].detections.map((det, i) => ({
      angle: det.azimuth,
      range: det.range,
      snr: det.snr,
      doppler: det.doppler,
      classification: sensorData.radar[0].tracks[i]?.classification || 'unknown'
    }));
  }, [sensorData]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  const containerStyle = {
    backgroundColor: '#495057', // Bootstrap dark gray
    padding: '16px',
    borderRadius: '12px',
    color: '#f8f9fa',
    marginBottom: '16px',
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  };

  const badgeStyle = {
    fontSize: '0.8rem',
    backgroundColor: '#28a745',
    padding: '4px 8px',
    borderRadius: '6px',
    color: '#fff',
  };

  const gridItemStyle = {
    fontSize: '0.9rem',
    marginBottom: '8px',
  };

  const getClassLabel = (cls) => {
    switch (cls) {
      case 'car': return '차량';
      case 'pedestrian': return '보행자';
      case 'traffic_sign': return '교통표지';
      default: return cls;
    }
  };

  const boxStyle = {
    backgroundColor: '#495057', // Bootstrap dark gray
    padding: '12px',
    borderRadius: '8px',
    height: '100%',
  };

  const labelStyle = {
    fontSize: '0.9rem',
    color: '#adb5bd', // muted gray
    marginBottom: '4px',
  };

  const valueStyle = {
    fontWeight: '600',
    fontSize: '1rem',
  };

  const sectionStyle = {
    marginBottom: '24px',
  };

  const sensorBoxStyle = (bgColor) => ({
    backgroundColor: bgColor,
    padding: '12px',
    borderRadius: '8px',
    backgroundBlendMode: 'overlay',
    height: '100%',
  });

  const itemStyle = {
    backgroundColor: '#495057',
    padding: '12px',
    borderRadius: '8px',
    color: '#f8f9fa',
    marginBottom: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const leftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const statusDotStyle = (status) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: status === 'ok' ? '#28a745' : '#dc3545',
  });

  const rightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '0.85rem',
  };


  return (
    <div>
      {/* 헤더 */}
      <div style={{ marginTop: '30px' }}>
        <h3>자율주행 센서 성능 분석 보고서</h3>
        <p>ROSBag 데이터 기반 성능 모니터링 및 분석</p>
      </div>

      {/* 탭 네비게이션 */}
      <div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {[
            { id: 'overview', label: '전체 개요' },
            { id: 'lidar', label: '라이더 분석' },
            { id: 'radar', label: '레이더 분석' },
            { id: 'camera', label: '카메라 분석' },
            { id: 'fusion', label: '센서 융합' },
            { id: 'performance', label: '성능 트렌드' }
          ].map(tab => (
            <Button
              key={tab.id}
              onClick={() => setSelectedAnalysis(tab.id)}
              className={`px-4 py-2 rounded-lg transition-colors ${selectedAnalysis === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      </div>

      {/* 전체 개요 */}
      {selectedAnalysis === 'overview' && (
        <div style={{ marginTop: '30px' }}>
          {/* KPI 카드 */}
          <div style={{ width: '50%', backgroundColor: '#eee', padding: '20px' }}>
            <Row className="g-4">
              <Col xs={12} md={6} lg={3}>
                <Card border="success" bg="dark" text="light" className="h-100">
                  <Card.Body>
                    <Card.Title className="text-muted small">라이더 품질</Card.Title>
                    <h2 className="text-success">{performanceMetrics.lidar.qualityScore.toFixed(1)}%</h2>
                    <Card.Text className="text-secondary small">완전성 지수</Card.Text>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Card border="warning" bg="dark" text="light" className="h-100">
                  <Card.Body>
                    <Card.Title className="text-muted small">레이더 정확도</Card.Title>
                    <h2 className="text-warning">{performanceMetrics.radar.trackingAccuracy.toFixed(1)}%</h2>
                    <Card.Text className="text-secondary small">추적 신뢰도</Card.Text>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Card border="primary" bg="dark" text="light" className="h-100">
                  <Card.Body>
                    <Card.Title className="text-muted small">카메라 검출</Card.Title>
                    <h2 className="text-primary">{performanceMetrics.camera.detectionAccuracy.toFixed(1)}%</h2>
                    <Card.Text className="text-secondary small">평균 신뢰도</Card.Text>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Card border="info" bg="dark" text="light" className="h-100">
                  <Card.Body>
                    <Card.Title className="text-muted small">시스템 안정성</Card.Title>
                    <h2 className="text-info">{performanceMetrics.fusion.systemReliability.toFixed(1)}%</h2>
                    <Card.Text className="text-secondary small">종합 신뢰도</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>

          {/* 센서 상태 및 객체 분포 */}
          <div style={{ marginTop: '30px' }}>
            {/* 센서 상태 */}
            <div>
              <h5>센서 상태 모니터링</h5>
              <ResponsiveContainer width="40%" height={300}>
                <BarChart data={sensorHealthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="sensor" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Bar dataKey="confidence" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* 객체 탐지 분포 */}
            <div>
              <h5>객체 탐지 분포</h5>
              <ResponsiveContainer width="40%" height={300}>
                <PieChart>
                  <Pie
                    data={detectionDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {detectionDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* 라이더 분석 */}
      {selectedAnalysis === 'lidar' && (
        <div style={{ marginTop: '30px' }}>
          <div style={{ width: '50%', padding: '20px' }}>
            {/* 포인트 클라우드 통계 */}
            <Card bg="dark" text="light" className="mb-4 p-4 rounded">
              <Card.Body>
                <Card.Title className="text-xl fw-semibold mb-4">포인트 클라우드 분석</Card.Title>
                <Row className="g-4">
                  <Col xs={12} md={6}>
                    <Card bg="secondary" text="light" className="h-100 p-3 rounded">
                      <Card.Text>총 포인트 수</Card.Text>
                      <h2 className="text-success">{sensorData.lidar[0].statistics.totalPoints.toLocaleString()}</h2>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card bg="secondary" text="light" className="h-100 p-3 rounded">
                      <Card.Text>유효 포인트</Card.Text>
                      <h2 className="text-primary">{sensorData.lidar[0].statistics.validPoints.toLocaleString()}</h2>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card bg="secondary" text="light" className="h-100 p-3 rounded">
                      <Card.Text>최대 거리</Card.Text>
                      <h2 className="text-warning">{sensorData.lidar[0].statistics.maxRange.toFixed(1)}m</h2>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card bg="secondary" text="light" className="h-100 p-3 rounded">
                      <Card.Text>평균 강도</Card.Text>
                      <h2 className="text-info">{sensorData.lidar[0].statistics.averageIntensity.toFixed(1)}</h2>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* 링별 포인트 분포 */}
            <div style={{ marginTop: '30px' }}>
              <h5>레이저 링별 포인트 분포</h5>
              <ResponsiveContainer width="50%" height={300}>
                <BarChart data={sensorData.lidar[0].statistics.pointsByRing.map((count, i) => ({
                  ring: `링 ${i}`,
                  points: count
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="ring" stroke="#9CA3AF" angle={-45} textAnchor="end" height={60} />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Bar dataKey="points" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 품질 메트릭 추이 */}
          <div>
            <h5>라이더 품질 추이</h5>
            <ResponsiveContainer width="50%" height={400}>
              <LineChart data={timeSeriesData.slice(-50)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                <Line type="monotone" dataKey="lidarQuality" stroke="#10B981" name="품질 지수" strokeWidth={2} />
                <Line type="monotone" dataKey="lidarNoise" stroke="#EF4444" name="노이즈 레벨" strokeWidth={2} />
                <Line type="monotone" dataKey="lidarIntensity" stroke="#3B82F6" name="평균 강도" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 레이더 분석 */}
      {selectedAnalysis === 'radar' && (
        <div style={{ marginTop: '30px' }}>
          <div>
            {/* 레이더 탐지 현황 */}
            <div style={{ width: '50%', padding: '20px' }}>
              <h5>탐지 객체 현황</h5>
              <div className="space-y-4">
                {sensorData.radar[0].tracks.map((track, i) => (
                  <div key={i} style={containerStyle}>
                    <div style={headerStyle}>
                      <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                        객체 #{track.id} - {track.classification === 'car' ? '차량' : '보행자'}
                      </div>
                      <div style={badgeStyle}>
                        {(track.confidence * 100).toFixed(1)}%
                      </div>
                    </div>
                    <Row>
                      <Col xs={6}><div style={gridItemStyle}>거리: {track.range.toFixed(1)}m</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>방위각: {track.azimuth.toFixed(1)}°</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>속도: {track.doppler.toFixed(1)} m/s</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>SNR: {track.snr.toFixed(1)} dB</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>RCS: {track.rcs.toFixed(1)} dBsm</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>추적 시간: {track.trackAge.toFixed(1)}s</div></Col>
                    </Row>
                  </div>
                ))}
              </div>
            </div>

            {/* 레이더 극좌표 플롯 */}
            <div>
              <h5>탐지 객체 위치 (극좌표)</h5>
              <ResponsiveContainer width="50%" height={300}>
                <ScatterChart
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid stroke="#374151" />
                  <XAxis
                    type="number"
                    dataKey="angle"
                    name="방위각"
                    unit="°"
                    stroke="#9CA3AF"
                    domain={[-30, 30]}
                  />
                  <YAxis
                    type="number"
                    dataKey="range"
                    name="거리"
                    unit="m"
                    stroke="#9CA3AF"
                    domain={[0, 60]}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-gray-700 p-3 rounded border">
                            <p className="text-white">방위각: {data.angle.toFixed(1)}°</p>
                            <p className="text-white">거리: {data.range.toFixed(1)}m</p>
                            <p className="text-white">SNR: {data.snr.toFixed(1)}dB</p>
                            <p className="text-white">도플러: {data.doppler.toFixed(1)}m/s</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter
                    name="탐지 객체"
                    data={radarPolarData}
                    fill="#F59E0B"
                    r={6}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 레이더 성능 메트릭 */}
          <div>
            <h5>레이더 성능 추이</h5>
            <ResponsiveContainer width="50%" height={400}>
              <LineChart data={timeSeriesData.slice(-50)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                <Line type="monotone" dataKey="radarTracks" stroke="#F59E0B" name="추적 객체 수" strokeWidth={2} />
                <Line type="monotone" dataKey="radarDetections" stroke="#EF4444" name="탐지 수" strokeWidth={2} />
                <Line type="monotone" dataKey="radarSnr" stroke="#10B981" name="평균 SNR" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 카메라 분석 */}
      {selectedAnalysis === 'camera' && (
        <div style={{ marginTop: '30px' }}>
          <div>
            {/* 이미지 품질 메트릭 */}
            <div>
              <h5>이미지 품질 분석</h5>
              <ResponsiveContainer width="40%" height={300}>
                <RadarChart data={[
                  {
                    metric: '밝기',
                    value: sensorData.camera[0].quality.brightness / 255 * 100,
                    fullMark: 100
                  },
                  {
                    metric: '대비',
                    value: sensorData.camera[0].quality.contrast * 50,
                    fullMark: 100
                  },
                  {
                    metric: '선명도',
                    value: sensorData.camera[0].quality.sharpness * 100,
                    fullMark: 100
                  },
                  {
                    metric: '채도',
                    value: sensorData.camera[0].quality.saturation * 50,
                    fullMark: 100
                  },
                  {
                    metric: '노이즈(역)',
                    value: (1 - sensorData.camera[0].quality.noiseLevel) * 100,
                    fullMark: 100
                  },
                  {
                    metric: '블러(역)',
                    value: (1 - sensorData.camera[0].quality.blur) * 100,
                    fullMark: 100
                  }
                ]}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={0} domain={[0, 100]} />
                  {/* <Radar
                    name="품질 지수"
                    dataKey="value"
                    stroke="#3B82F6"
                    fill="#3B82F6"
                    fillOpacity={0.3}
                  /> */}
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* 검출 객체 상세 정보 */}
            <div>
              <h5>검출 객체 상세 정보</h5>
              <div style={{ width: '50%', padding: '20px' }}>
                {sensorData.camera[0].detections.map((detection, i) => (
                  <div key={i} style={containerStyle}>
                    <div style={headerStyle}>
                      <div style={{ fontWeight: '600' }}>{getClassLabel(detection.class)}</div>
                      <div style={badgeStyle}>{(detection.confidence * 100).toFixed(1)}%</div>
                    </div>
                    <Row>
                      <Col xs={6}><div style={gridItemStyle}>위치: ({detection.bbox.x}, {detection.bbox.y})</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>크기: {detection.bbox.width}×{detection.bbox.height}</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>월드 X: {detection.worldPosition.x.toFixed(1)}m</div></Col>
                      <Col xs={6}><div style={gridItemStyle}>월드 Y: {detection.worldPosition.y.toFixed(1)}m</div></Col>
                      {detection.attributes && (
                        <Col xs={12}>
                          <div style={{ fontSize: '0.8rem', color: '#ced4da' }}>
                            속성: {JSON.stringify(detection.attributes)}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 카메라 성능 추이 */}
          <div>
            <h5>카메라 성능 추이</h5>
            <ResponsiveContainer width="50%" height={400}>
              <LineChart data={timeSeriesData.slice(-50)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                <Line type="monotone" dataKey="cameraDetections" stroke="#3B82F6" name="검출 객체 수" strokeWidth={2} />
                <Line type="monotone" dataKey="cameraConfidence" stroke="#10B981" name="평균 신뢰도" strokeWidth={2} />
                <Line type="monotone" dataKey="cameraSharpness" stroke="#F59E0B" name="선명도" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* 카메라 설정 정보 */}
          <div>
            <h5>카메라 설정 및 캘리브레이션</h5>
            <div>
              <div>
                <h6>이미지 설정</h6>
                <div style={{ width: '50%', padding: '20px' }}>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <div style={boxStyle}>
                        <div style={labelStyle}>해상도</div>
                        <div style={valueStyle}>{sensorData.camera[0].image.width}×{sensorData.camera[0].image.height}</div>
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div style={boxStyle}>
                        <div style={labelStyle}>노출시간</div>
                        <div style={valueStyle}>{sensorData.camera[0].image.exposure}ms</div>
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div style={boxStyle}>
                        <div style={labelStyle}>게인</div>
                        <div style={valueStyle}>{sensorData.camera[0].image.gain}</div>
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div style={boxStyle}>
                        <div style={labelStyle}>조리개</div>
                        <div style={valueStyle}>f/{sensorData.camera[0].image.aperture}</div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* 센서 융합 */}
      {selectedAnalysis === 'fusion' && (
        <div style={{ marginTop: '30px' }}>
          {/* 융합 객체 정보 */}
          <div>
            <h5>센서 융합 객체</h5>
            <div style={{ width: '50%', padding: '20px' }}>
              {sensorData.fusion[0].objects.map((obj, i) => (
                <div key={i} style={containerStyle}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div>
                      <h4 style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                        융합 객체 #{obj.id} - {obj.type === 'vehicle' ? '차량' : obj.type}
                      </h4>
                      <div style={{ fontSize: '0.9rem', color: '#dee2e6' }}>
                        추적 시간: {obj.trackingInfo.age.toFixed(1)}초
                      </div>
                    </div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#28a745' }}>
                      {(obj.confidence * 100).toFixed(1)}%
                    </div>
                  </div>

                  {/* 위치, 속도, 크기 */}
                  <Row className="g-4" style={sectionStyle}>
                    <Col xs={12} md={4}>
                      <div style={boxStyle}>
                        <div style={labelStyle}>위치 (m)</div>
                        <div>X: {obj.position.x.toFixed(1)}</div>
                        <div>Y: {obj.position.y.toFixed(1)}</div>
                        <div>Z: {obj.position.z.toFixed(1)}</div>
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={boxStyle}>
                        <div style={labelStyle}>속도 (m/s)</div>
                        <div>VX: {obj.velocity.x.toFixed(1)}</div>
                        <div>VY: {obj.velocity.y.toFixed(1)}</div>
                        <div>VZ: {obj.velocity.z.toFixed(1)}</div>
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={boxStyle}>
                        <div style={labelStyle}>크기 (m)</div>
                        <div>L: {obj.dimensions.length.toFixed(1)}</div>
                        <div>W: {obj.dimensions.width.toFixed(1)}</div>
                        <div>H: {obj.dimensions.height.toFixed(1)}</div>
                      </div>
                    </Col>
                  </Row>

                  {/* 센서별 검출 정보 */}
                  <div style={{ borderTop: '1px solid #6c757d', paddingTop: '16px', ...sectionStyle }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px' }}>센서별 검출 정보</div>
                    <Row className="g-4">
                      <Col xs={12} md={4}>
                        <div style={sensorBoxStyle('rgba(0,128,0,0.2)')}>
                          <div style={{ color: '#28a745', fontWeight: '600' }}>라이더</div>
                          <div style={{ fontSize: '0.9rem' }}>신뢰도: {(obj.sensorDetections.lidar.confidence * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: '0.9rem' }}>포인트: {obj.sensorDetections.lidar.pointCount}개</div>
                        </div>
                      </Col>
                      <Col xs={12} md={4}>
                        <div style={sensorBoxStyle('rgba(255,193,7,0.2)')}>
                          <div style={{ color: '#ffc107', fontWeight: '600' }}>레이더</div>
                          <div style={{ fontSize: '0.9rem' }}>신뢰도: {(obj.sensorDetections.radar.confidence * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: '0.9rem' }}>SNR: {obj.sensorDetections.radar.snr}dB</div>
                        </div>
                      </Col>
                      <Col xs={12} md={4}>
                        <div style={sensorBoxStyle('rgba(0,123,255,0.2)')}>
                          <div style={{ color: '#0d6efd', fontWeight: '600' }}>카메라</div>
                          <div style={{ fontSize: '0.9rem' }}>신뢰도: {(obj.sensorDetections.camera.confidence * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: '0.9rem' }}>박스: {obj.sensorDetections.camera.bbox.join('×')}</div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* 추적 품질 지표 */}
                  <div style={{ borderTop: '1px solid #6c757d', paddingTop: '16px', marginTop: '16px' }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px' }}>추적 품질 지표</div>
                    <div style={{ display: 'flex', gap: '24px', fontSize: '0.9rem' }}>
                      <div>예측 오차: <span style={{ color: '#fd7e14' }}>{obj.trackingInfo.predictionError.toFixed(3)}</span></div>
                      <div>연관 점수: <span style={{ color: '#28a745' }}>{(obj.trackingInfo.associationScore * 100).toFixed(1)}%</span></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 센서 동기화 상태 */}
          <div>
            <div>
              <h5>센서 동기화 상태</h5>
              <div style={{ width: '30%', padding: '20px' }}>
                {Object.entries(sensorData.fusion[0].sensorHealth).map(([sensor, health]) => (
                  <div key={sensor} style={itemStyle}>
                    <div style={leftStyle}>
                      <div style={statusDotStyle(health.status)}></div>
                      <div style={{ fontWeight: '500', textTransform: 'capitalize' }}>{sensor}</div>
                    </div>
                    <div style={rightStyle}>
                      <div>{(health.confidence * 100).toFixed(1)}%</div>
                      <div style={{ color: '#adb5bd', fontSize: '0.75rem' }}>
                        {new Date(health.lastUpdate * 1000).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 융합 성능 메트릭 */}
            <div>
              <h5>융합 성능 메트릭</h5>
              <div style={{ width: '30%', padding: '20px' }}>
                <div style={containerStyle}>
                  <div style={boxStyle}>
                    <div style={labelStyle}>전체 정확도</div>
                    <div style={valueStyle}>
                      {performanceMetrics.fusion.overallAccuracy.toFixed(1)}%
                    </div>
                  </div>
                  <div style={boxStyle}>
                    <div style={labelStyle}>센서 간 일치도</div>
                    <div style={valueStyle}>
                      {(performanceMetrics.fusion.sensorAgreement * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div style={boxStyle}>
                    <div style={labelStyle}>추적 일관성</div>
                    <div style={valueStyle}>
                      {performanceMetrics.fusion.trackingConsistency.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 성능 트렌드 */}
      {selectedAnalysis === 'performance' && (
        <div style={{ marginTop: '30px' }}>
          {/* 전체 시스템 성능 추이 */}
          <div>
            <h5>전체 시스템 성능 추이</h5>
            <ResponsiveContainer width="50%" height={400}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Legend />
                <Line type="monotone" dataKey="fusionAccuracy" stroke="#10B981" name="융합 정확도" strokeWidth={2} />
                <Line type="monotone" dataKey="fusionLatency" stroke="#EF4444" name="처리 지연시간" strokeWidth={2} />
                <Line type="monotone" dataKey="systemLoad" stroke="#3B82F6" name="시스템 부하" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* 성능 히스토그램 */}
          <div>
            <div>
              <h5>지연시간 분포</h5>
              <ResponsiveContainer width="50%" height={300}>
                <BarChart data={[
                  { range: '0-10ms', count: 15 },
                  { range: '10-20ms', count: 25 },
                  { range: '20-30ms', count: 35 },
                  { range: '30-40ms', count: 45 },
                  { range: '40-50ms', count: 30 },
                  { range: '50ms+', count: 10 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="range" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Bar dataKey="count" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h5>정확도 분포</h5>
              <ResponsiveContainer width="50%" height={300}>
                <BarChart data={[
                  { range: '80-85%', count: 5 },
                  { range: '85-90%', count: 15 },
                  { range: '90-95%', count: 45 },
                  { range: '95-98%', count: 25 },
                  { range: '98%+', count: 10 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="range" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Bar dataKey="count" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 성능 요약 테이블 */}
          <div>
            <h5>성능 요약 보고서</h5>
            <div style={{ width: '30%', padding: '20px' }}>
              <Table striped bordered hover variant="dark" size="sm" className="mb-4">
                <thead>
                  <tr>
                    <th>센서</th>
                    <th>정확도</th>
                    <th>지연시간</th>
                    <th>데이터율</th>
                    <th>품질 점수</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>라이더</strong></td>
                    <td style={{ color: '#28a745' }}>{performanceMetrics.lidar.qualityScore.toFixed(1)}%</td>
                    <td>15ms</td>
                    <td>{performanceMetrics.lidar.dataRate}Hz</td>
                    <td style={{ color: '#28a745' }}>A+</td>
                    <td><span style={{ color: '#28a745' }}>●</span> 정상</td>
                  </tr>
                  <tr>
                    <td><strong>레이더</strong></td>
                    <td style={{ color: '#ffc107' }}>{performanceMetrics.radar.trackingAccuracy.toFixed(1)}%</td>
                    <td>8ms</td>
                    <td>20Hz</td>
                    <td style={{ color: '#ffc107' }}>B+</td>
                    <td><span style={{ color: '#28a745' }}>●</span> 정상</td>
                  </tr>
                  <tr>
                    <td><strong>카메라</strong></td>
                    <td style={{ color: '#0d6efd' }}>{performanceMetrics.camera.detectionAccuracy.toFixed(1)}%</td>
                    <td>{performanceMetrics.camera.processingLatency}ms</td>
                    <td>30Hz</td>
                    <td style={{ color: '#0d6efd' }}>A-</td>
                    <td><span style={{ color: '#28a745' }}>●</span> 정상</td>
                  </tr>
                  <tr>
                    <td><strong>융합 시스템</strong></td>
                    <td style={{ color: '#6f42c1' }}>{performanceMetrics.fusion.overallAccuracy.toFixed(1)}%</td>
                    <td>45ms</td>
                    <td>10Hz</td>
                    <td style={{ color: '#6f42c1' }}>A+</td>
                    <td><span style={{ color: '#28a745' }}>●</span> 정상</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultReport;